import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Target, Trophy, TrendingUp, Plus, CheckCircle, MoreVertical, X } from 'lucide-react';
import { format, isToday, isBefore, isAfter, isSameDay } from 'date-fns';
import { useHabits } from '@/hooks/use-habits';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/use-organization';
import { AddHabitDialog } from './AddHabitDialog';
import { EditHabitDialog } from './EditHabitDialog';
import type { Habit } from '@/types/habits';
import { cn } from '@/lib/utils';
import { getBadgeForDays } from '@/lib/constants/habitBadges';
import { formatDateForArabic } from '@/lib/utils/dates';
import { calculateTotalDots, getFrequencyDisplayText } from '@/lib/utils/habitProgress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { deleteHabit, getHabitDisplayName, isHabitLoggedOnDate, getHabitCompletionCountToday, getHabitCompletionCountOnDate, getHabitLogs } from '@/lib/firebase/queries/habits';
import { 
  getActiveCompetitionsForUser, 
  logCompetitionHabit, 
  unlogCompetitionHabit, 
  isCompetitionHabitLoggedToday 
} from '@/lib/firebase/queries/competitions';
import type { Competition } from '@/types/competitions';
import { notifyCompetitionLogChanged } from '@/lib/competitions/competitionLogEvents';
import { habitsDoneCount } from '@/lib/competitions/standingsView';
import { toast } from 'sonner';

interface HabitTrackerProps {
  selectedDate?: Date;
  organizationId?: string;
  /** The beneficiary's own habits; off when the organization hides habitTracking. */
  showPersonalHabits?: boolean;
  /**
   * Competition habits in the same list, each with a trophy chip — mobile's
   * TodayScreen (namat#36). Pass hasSectionAccess('organizationCompetitions'):
   * when off, no competition is read or shown.
   */
  showCompetitionHabits?: boolean;
}

export function HabitTracker({
  selectedDate = new Date(),
  organizationId,
  showPersonalHabits = true,
  showCompetitionHabits = false,
}: HabitTrackerProps) {
  const [showAddHabit, setShowAddHabit] = useState(false);
  const [habitToEdit, setHabitToEdit] = useState<Habit | null>(null);
  const [habitToDelete, setHabitToDelete] = useState<Habit | null>(null);
  const [hoveredButtons, setHoveredButtons] = useState<Record<string, boolean>>({});
  const [habitCompletionStatus, setHabitCompletionStatus] = useState<Record<string, boolean>>({});
  const [habitCompletionCounts, setHabitCompletionCounts] = useState<Record<string, number>>({});
  const [habitProgressDots, setHabitProgressDots] = useState<Record<string, number[]>>({});
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [competitionHabitStatus, setCompetitionHabitStatus] = useState<Record<string, boolean>>({});
  const [competitionProgressDots, setCompetitionProgressDots] = useState<Record<string, boolean[]>>({});
  const [loadingCompetitions, setLoadingCompetitions] = useState(true);
  // Re-entrancy guard for a competition habit's button: a second click while
  // the first write is in flight would log twice or log-then-unlog.
  const [busyCompetitionHabit, setBusyCompetitionHabit] = useState<string | null>(null);
  const { habits, loading, logHabit, unlogHabit, refreshHabits } = useHabits();
  const { t, language } = useLanguage();
  const { currentUser } = useAuth();
  const { selectedOrganization } = useOrganization();

  // Fetch active competitions
  useEffect(() => {
    const fetchCompetitions = async () => {
      if (!currentUser?.uid || !organizationId || !showCompetitionHabits) {
        setCompetitions([]);
        setCompetitionHabitStatus({});
        setCompetitionProgressDots({});
        setLoadingCompetitions(false);
        return;
      }

      try {
        setLoadingCompetitions(true);
        // Clear previous competition data when switching organizations
        setCompetitions([]);
        setCompetitionHabitStatus({});
        setCompetitionProgressDots({});
        
        const activeCompetitions = await getActiveCompetitionsForUser(
          currentUser.uid, 
          organizationId
        );
        setCompetitions(activeCompetitions);
      } catch (error) {
        console.error('Error fetching competitions:', error);
        setCompetitions([]);
        setCompetitionHabitStatus({});
        setCompetitionProgressDots({});
      } finally {
        setLoadingCompetitions(false);
      }
    };

    fetchCompetitions();
  }, [currentUser?.uid, organizationId, showCompetitionHabits]);

  // Check competition habit completion status
  useEffect(() => {
    const checkCompetitionStatus = async () => {
      if (!currentUser?.uid || competitions.length === 0) return;

      const statusMap: Record<string, boolean> = {};
      
      await Promise.all(
        competitions.flatMap(competition =>
          competition.habits.map(async (habit) => {
            const key = `${competition.id}_${habit.id}`;
            const isCompleted = await isCompetitionHabitLoggedToday(
              competition.id,
              habit.id,
              currentUser.uid
            );
            statusMap[key] = isCompleted;
          })
        )
      );

      setCompetitionHabitStatus(statusMap);
    };

    checkCompetitionStatus();
  }, [competitions, selectedDate, currentUser?.uid]);

  // Load competition habit progress dots
  useEffect(() => {
    const loadCompetitionProgressDots = async () => {
      if (!currentUser?.uid || competitions.length === 0) return;
      
      const progressDotsMap: Record<string, boolean[]> = {};
      
      await Promise.all(
        competitions.flatMap(competition =>
          competition.habits.map(async (habit) => {
            const key = `${competition.id}_${habit.id}`;
            const startDate = new Date(competition.startDate);
            const endDate = new Date(competition.endDate);
            const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
            
            const dots = Array(totalDays).fill(false);
            
            // Check each day of the competition
            for (let i = 0; i < totalDays; i++) {
              const date = new Date(startDate);
              date.setDate(startDate.getDate() + i);
              date.setHours(0, 0, 0, 0);
              
              // Only check past dates and today
              if (date <= new Date()) {
                try {
                  // For competition habits, we need to check if it was logged on this specific date
                  // Since we don't have a date-specific function, we'll use the today function for now
                  // and improve this later with a proper date-specific function
                  const isCompleted = date.toDateString() === new Date().toDateString() 
                    ? await isCompetitionHabitLoggedToday(competition.id, habit.id, currentUser.uid)
                    : false; // For now, only show today's completion
                  dots[i] = isCompleted;
                } catch (error) {
                  console.error('Error checking competition habit log:', error);
                  dots[i] = false;
                }
              }
            }
            
            progressDotsMap[key] = dots;
          })
        )
      );
      
      setCompetitionProgressDots(progressDotsMap);
    };

    loadCompetitionProgressDots();
  }, [competitions, currentUser?.uid]);

  // Check habit completion status for the selected date
  useEffect(() => {
    const checkCompletionStatus = async () => {
      if (!currentUser?.uid || habits.length === 0) return;
      
      const statusMap: Record<string, boolean> = {};
      const countsMap: Record<string, number> = {};
      
      await Promise.all(
        habits.map(async (habit) => {
          const count = await getHabitCompletionCountOnDate(habit.id, currentUser.uid, selectedDate);
          const isCompleted = count >= habit.frequencyConfig.timesPerFrequency;
          statusMap[habit.id] = isCompleted;
          countsMap[habit.id] = count;
        })
      );
      
      setHabitCompletionStatus(statusMap);
      setHabitCompletionCounts(countsMap);
    };

    checkCompletionStatus();
  }, [habits, selectedDate, currentUser?.uid]);

  // Load progress dots data for all habits
  useEffect(() => {
    console.log('🔄 useEffect triggered - currentUser:', currentUser?.uid, 'habits.length:', habits.length);
    
    let isMounted = true; // Track if component is still mounted
    
    const loadProgressDots = async () => {
      if (!currentUser?.uid || habits.length === 0) {
        console.log('❌ Skipping progress dots load - missing user or habits');
        return;
      }
      
      console.log('✅ Loading progress dots for', habits.length, 'habits');
      
      try {
        const progressDotsMap: Record<string, number[]> = {};
        
        // Process habits in parallel
        await Promise.all(
          habits.map(async (habit) => {
            try {
              const totalDots = calculateTotalDots(habit.frequencyConfig);
              const dots = Array(totalDots).fill(0);
              const startDate = new Date(habit.createdAt);
              startDate.setHours(0, 0, 0, 0);
              
              console.log(`📊 Habit "${habit.name}": totalDots=${totalDots}, duration=${habit.frequencyConfig.duration}, startDate=${format(startDate, 'yyyy-MM-dd')}, target=${habit.frequencyConfig.timesPerFrequency}/period`);
              
              // OPTIMIZATION: Fetch ALL logs for this habit at once (1 query instead of 90!)
              const logs = await getHabitLogs(habit.id, currentUser.uid, habit.frequencyConfig.duration);
              console.log(`   📥 Fetched ${logs.length} logs for "${habit.name}"`);
              
              // Log the actual dates we have data for
              if (logs.length > 0) {
                const logDates = logs.map(l => l.date).sort();
                console.log(`   📅 Log dates: [${logDates.join(', ')}]`);
              }
              
              // Create a map for fast lookup
              const logsMap = new Map(logs.map(log => [log.date, log]));
              
              const mapKeys = Array.from(logsMap.keys());
              console.log(`   🗺️ LogsMap has ${mapKeys.length} entries with keys: [${mapKeys.slice(0, 10).join(', ')}]${mapKeys.length > 10 ? '...' : ''}`);
              
              let completedDaysCount = 0;
              
              // Process each day using the pre-fetched logs
              for (let i = 0; i < habit.frequencyConfig.duration && i < totalDots; i++) {
                const date = new Date(startDate);
                date.setDate(startDate.getDate() + i);
                const dateStr = format(date, 'yyyy-MM-dd');
                
                const log = logsMap.get(dateStr);
                // Handle both new (completionCount) and old (completed) log formats
                const completionCount = log?.completionCount ?? (log?.completed ? 1 : 0);
                
                // Debug lookup for first 10 days
                if (i < 10 && logs.length > 0) {
                  console.log(`   🔍 Day ${i} looking for "${dateStr}" → ${log ? `FOUND (count=${completionCount})` : 'NOT FOUND'}`);
                }
                
                // Dot is filled only when target is fully met
                const isMet = completionCount >= habit.frequencyConfig.timesPerFrequency;
                dots[i] = isMet ? 1 : 0;
                if (isMet) completedDaysCount++;
              }
              
              console.log(`✅ Habit "${habit.name}": ${completedDaysCount}/${totalDots} days completed`);
              progressDotsMap[habit.id] = dots;
            } catch (habitError) {
              console.error(`❌ Error processing habit ${habit.name}:`, habitError);
              // Set empty dots on error to prevent UI issues
              progressDotsMap[habit.id] = Array(calculateTotalDots(habit.frequencyConfig)).fill(0);
            }
          })
        );
        
        // Only update state if component is still mounted
        if (isMounted) {
          console.log('💾 Setting progress dots for', Object.keys(progressDotsMap).length, 'habits');
          setHabitProgressDots(progressDotsMap);
          console.log('✨ Progress dots state updated successfully!');
        } else {
          console.log('⚠️ Component unmounted, skipping state update');
        }
      } catch (error) {
        console.error('❌ Error loading progress dots:', error);
      }
    };

    loadProgressDots();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [currentUser?.uid, habits.map(h => h.id).join(',')]); // Reload when habit IDs change

  const handleHabitToggle = async (habitId: string, isCompleted: boolean) => {
    if (!currentUser?.uid) return;

    const habit = habits.find(h => h.id === habitId);
    if (!habit) return;

    try {
      // First, perform the log/unlog action
      if (isCompleted) {
        await unlogHabit(habitId, currentUser.uid);
      } else {
        await logHabit(habitId, currentUser.uid);
      }

      // Immediately get the new completion count for today
      const newCount = await getHabitCompletionCountOnDate(habitId, currentUser.uid, selectedDate);
      
      // Update completion status and count immediately
      setHabitCompletionStatus(prev => ({
        ...prev,
        [habitId]: newCount >= habit.frequencyConfig.timesPerFrequency
      }));
      setHabitCompletionCounts(prev => ({
        ...prev,
        [habitId]: newCount
      }));

      // Optimized: Update only the dot for today's date instead of all days
      const totalDots = calculateTotalDots(habit.frequencyConfig);
      const currentDots = habitProgressDots[habit.id] || Array(totalDots).fill(0);
      const newDots = [...currentDots];
      
      // Calculate which dot index corresponds to today
      const startDate = new Date(habit.createdAt);
      startDate.setHours(0, 0, 0, 0);
      const today = new Date(selectedDate);
      today.setHours(0, 0, 0, 0);
      const daysSinceStart = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      
      // For all frequencies: one dot per time unit, only filled when target is fully met
      if (daysSinceStart >= 0 && daysSinceStart < totalDots) {
        // Dot is filled (1) only when completions >= target, otherwise unfilled (0)
        newDots[daysSinceStart] = newCount >= habit.frequencyConfig.timesPerFrequency ? 1 : 0;
      }
      
      // Immediately update the UI
      setHabitProgressDots(prev => ({
        ...prev,
        [habitId]: newDots
      }));

      // Show success toast
      if (isCompleted) {
        toast.success(t('habits.notifications.unloggedSuccess'));
      } else {
        toast.success(t('habits.notifications.loggedSuccess'));
      }

      // Refresh habits in background to update streak, daysCompleted, etc.
      refreshHabits();
      
    } catch (error: any) {
      console.error('Error toggling habit:', error);
      if (error.message === 'already_logged') {
        toast.error(t('habits.notifications.alreadyLogged'));
      } else if (error.message === 'not_logged_today') {
        toast.error(t('habits.notifications.notLoggedToday'));
      } else if (error.message === 'max_completions_reached') {
        toast.error(t('habits.notifications.maxCompletionsReached', { count: habit.frequencyConfig.timesPerFrequency }));
      } else {
        toast.error(t('habits.notifications.logError'));
      }
    }
  };

  const handleButtonHover = (habitId: string, isHovered: boolean) => {
    setHoveredButtons(prev => ({
      ...prev,
      [habitId]: isHovered
    }));
  };

  const handleDeleteHabit = async () => {
    if (!habitToDelete) return;

    try {
      await deleteHabit(habitToDelete.id);
      toast.success(t('habits.notifications.habitDeleted'));
      refreshHabits();
    } catch (error) {
      console.error('Error deleting habit:', error);
      toast.error(t('habits.notifications.deleteError'));
    } finally {
      setHabitToDelete(null);
    }
  };

  // Get habits for the selected date
  const filteredHabits = habits.filter(habit => {
    const habitCreationDate = new Date(habit.createdAt);
    return isBefore(habitCreationDate, selectedDate) || isSameDay(habitCreationDate, selectedDate);
  });

  // Sort habits by creation date, newest first
  const sortedHabits = [...filteredHabits].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const handleCompetitionHabitToggle = async (
    competition: Competition,
    habitId: string,
    habitName: string,
    habitCategory: string,
    isCompleted: boolean
  ) => {
    const key = `${competition.id}_${habitId}`;
    if (!currentUser?.uid || busyCompetitionHabit === key) return;
    setBusyCompetitionHabit(key);

    let nowCompleted = isCompleted;
    try {
      if (isCompleted) {
        await unlogCompetitionHabit(competition.id, habitId, currentUser.uid);
        nowCompleted = false;
        toast.success(t('dashboard.competition.unlogged'));
      } else {
        await logCompetitionHabit(
          competition.id,
          habitId,
          currentUser.uid,
          competition.organizationId,
          habitName,
          habitCategory,
          'manual'
        );
        nowCompleted = true;
        toast.success(t('habits.notifications.loggedSuccess'));
      }
    } catch (error) {
      // Already logged (e.g. from the phone) is the state the click asked
      // for: show it as done instead of failing, as mobile does.
      if (error instanceof Error && error.message.includes('already logged')) {
        nowCompleted = true;
      } else {
        console.error('Error toggling competition habit:', error);
        toast.error(t('dashboard.competition.updateError'));
      }
    } finally {
      setCompetitionHabitStatus(prev => ({ ...prev, [key]: nowCompleted }));

      // Update the progress dot for today
      const startDate = new Date(competition.startDate);
      startDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysSinceStart = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      setCompetitionProgressDots(prev => {
        const currentDots = prev[key] || [];
        if (daysSinceStart < 0 || daysSinceStart >= currentDots.length) return prev;
        const newDots = [...currentDots];
        newDots[daysSinceStart] = nowCompleted;
        return { ...prev, [key]: newDots };
      });

      // The results card re-reads its standings, as mobile does after every toggle.
      notifyCompetitionLogChanged(competition.id);
      setBusyCompetitionHabit(null);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      Exercises: 'bg-blue-100 text-blue-800',
      Nutrition: 'bg-green-100 text-green-800',
      Sleep: 'bg-purple-100 text-purple-800',
      Stress: 'bg-orange-100 text-orange-800',
      Relations: 'bg-pink-100 text-pink-800',
      Addictions: 'bg-red-100 text-red-800',
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const categoryLabel = (category: string) => {
    const key = `habits.categories.${category}`;
    const label = t(key);
    return label === key ? category : label;
  };

  const isFutureDate = isAfter(selectedDate, new Date());
  const isTodaySelected = isToday(selectedDate);

  const competitionHabitKeys = competitions.flatMap(c => c.habits.map(h => `${c.id}_${h.id}`));
  // «x/y مكتمل» over the whole list, personal and competition (mobile TodayScreen).
  const progress = habitsDoneCount([
    ...(showPersonalHabits ? sortedHabits.map(h => habitCompletionStatus[h.id] || false) : []),
    ...competitionHabitKeys.map(key => competitionHabitStatus[key] || false),
  ]);

  // Competitions only (habit tracking hidden) and none running: no empty card.
  if (!showPersonalHabits && !loadingCompetitions && competitionHabitKeys.length === 0) {
    return null;
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b bg-muted/50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            {t('habits.habitTracking')}
          </CardTitle>
          <div className="flex items-center gap-3">
            {progress.total > 0 && (
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {t('habits.completedCount', { done: progress.done, total: progress.total })}
              </span>
            )}
            {showPersonalHabits && (
              <Button variant="outline" onClick={() => setShowAddHabit(true)} className="bg-white hover:bg-gray-100">
                <Plus className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">{t('habits.addNewHabit')}</span>
                <span className="sm:hidden">{t('habits.actions.add')}</span>
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-6">
          {/* Personal Habits */}
          {showPersonalHabits && sortedHabits.map((habit, index) => {
            const isCompletedOnDate = habitCompletionStatus[habit.id] || false;
            const completionCount = habitCompletionCounts[habit.id] || 0;
            const targetCount = habit.frequencyConfig.timesPerFrequency;
            const badge = getBadgeForDays(habit.category, habit.daysCompleted, t);

            return (
              <motion.div
                key={habit.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="space-y-2 border rounded-lg p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="space-y-1">
                    {/* Mobile: Name wraps with 3-dot menu on the same row */}
                    <div className="sm:hidden">
                      <div className="grid grid-cols-[1fr,auto] gap-2 mb-2">
                        <div>
                          <span className="font-medium whitespace-normal break-words">{getHabitDisplayName(habit, currentUser?.uid, t)}</span>
                          {(habit.description || (language === 'ar' && habit.descriptionAr)) && (
                            <p className="text-xs text-muted-foreground mt-1 whitespace-normal break-words">
                              {language === 'ar' && habit.descriptionAr ? habit.descriptionAr : habit.description}
                            </p>
                          )}
                        </div>
                        <div className="justify-self-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setHabitToEdit(habit)}>
                                  {t('habits.actions.edit')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => setHabitToDelete(habit)}
                                  className="text-red-600"
                                >
                                  {t('habits.actions.delete')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                      </div>
                    </div>

                    {/* Desktop: Original habit name and badges */}
                    <div className="hidden sm:flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{getHabitDisplayName(habit, currentUser?.uid, t)}</span>
                        <Badge variant="secondary" className="text-xs">
                          <Trophy className="h-3 w-3 mr-1" />
                          {habit.streak} {t('habits.dayStreak')}
                        </Badge>
                      </div>
                      {(habit.description || (language === 'ar' && habit.descriptionAr)) && (
                        <p className="text-xs text-muted-foreground whitespace-normal break-words">
                          {language === 'ar' && habit.descriptionAr ? habit.descriptionAr : habit.description}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Badge variant="outline" className="font-normal">
                          {badge.name}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {t('habits.startedOn', { 
                            date: language === 'ar' 
                              ? formatDateForArabic(new Date(habit.createdAt))
                              : format(new Date(habit.createdAt), 'PP')
                          })}
                        </span>
                      </div>
                      {/* Mobile-only log button at end of this row */}
                      <div className="sm:hidden">
                        <Button
                          variant="ghost"
                          onClick={() => handleHabitToggle(habit.id, isCompletedOnDate)}
                          onMouseEnter={() => handleButtonHover(habit.id, true)}
                          onMouseLeave={() => handleButtonHover(habit.id, false)}
                          className={cn(
                            "h-8 px-3",
                            isCompletedOnDate
                              ? hoveredButtons[habit.id]
                                ? "bg-red-500 hover:bg-red-600 text-white"
                                : "bg-[#7dda55] hover:bg-red-500 text-white opacity-80"
                              : "bg-[#7dda55] hover:bg-[#6dc447] text-white"
                          )}
                          disabled={isFutureDate || (() => {
                            const startDate = new Date(habit.createdAt);
                            const daysSinceCreation = Math.floor((selectedDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                            return daysSinceCreation >= habit.frequencyConfig.duration;
                          })()}
                        >
                          <motion.div
                            className="flex items-center"
                            whileTap={{ scale: 0.95 }}
                            transition={{ type: "spring", stiffness: 400, damping: 17 }}
                          >
                            {isCompletedOnDate ? (
                              hoveredButtons[habit.id] ? (
                                <>
                                  <X className="h-3 w-3 mr-1 text-white" />
                                  <span className="text-xs text-white">{t('habits.actions.unlogHabit')}</span>
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  <span className="text-xs">{t('habits.loggedOnDate')}</span>
                                </>
                              )
                            ) : isFutureDate ? (
                              <span className="text-xs">{t('habits.notifications.cannotLogFuture')}</span>
                            ) : (() => {
                              const startDate = new Date(habit.createdAt);
                              const daysSinceCreation = Math.floor((selectedDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                              if (daysSinceCreation >= habit.frequencyConfig.duration) {
                                return (
                                  <>
                                    <Trophy className="h-3 w-3 mr-1" />
                                    <span className="text-xs">{t('habits.finished')}</span>
                                  </>
                                );
                              }
                              return (
                                <>
                                  <TrendingUp className="h-3 w-3 mr-1" />
                                  <span className="text-xs">
                                    {targetCount > 1 
                                      ? `${t('habits.logForDate')} (${completionCount}/${targetCount})`
                                      : (isTodaySelected ? t('habits.logForDate') : t('habits.logForDate', { date: format(selectedDate, 'PP') }))
                                    }
                                  </span>
                                </>
                              );
                            })()}
                          </motion.div>
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Desktop: Original button layout */}
                  <div className="hidden sm:flex items-center justify-end gap-2 w-full sm:w-auto">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setHabitToEdit(habit)}>
                            {t('habits.actions.edit')}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => setHabitToDelete(habit)}
                            className="text-red-600"
                          >
                            {t('habits.actions.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    <Button 
                      variant="ghost" 
                      onClick={() => handleHabitToggle(habit.id, isCompletedOnDate)}
                      onMouseEnter={() => handleButtonHover(habit.id, true)}
                      onMouseLeave={() => handleButtonHover(habit.id, false)}
                      className={cn(
                        "h-9 px-4 flex-1 sm:flex-none",
                        isCompletedOnDate
                          ? hoveredButtons[habit.id]
                            ? "bg-red-500 hover:bg-red-600 text-white"
                            : "bg-[#7dda55] hover:bg-red-500 text-white opacity-80"
                          : "bg-[#7dda55] hover:bg-[#6dc447] text-white"
                      )}
                      disabled={isFutureDate || (() => {
                        const startDate = new Date(habit.createdAt);
                        const daysSinceCreation = Math.floor((selectedDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                        return daysSinceCreation >= habit.frequencyConfig.duration;
                      })()}
                    >
                      <motion.div
                        className="flex items-center"
                        whileTap={{ scale: 0.95 }}
                        transition={{ type: "spring", stiffness: 400, damping: 17 }}
                      >
                        {isCompletedOnDate ? (
                          hoveredButtons[habit.id] ? (
                            <>
                              <X className="h-4 w-4 mr-2 text-white" />
                              <span className="hidden sm:inline text-white">{t('habits.actions.unlogHabit')}</span>
                              <span className="sm:hidden text-white">{t('habits.actions.unlogHabit')}</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              <span className="hidden sm:inline">{t('habits.loggedOnDate')}</span>
                              <span className="sm:hidden">Logged</span>
                            </>
                          )
                        ) : isFutureDate ? (
                          <>
                            <span className="hidden sm:inline">{t('habits.notifications.cannotLogFuture')}</span>
                            <span className="sm:hidden">Future</span>
                          </>
                        ) : (() => {
                          const startDate = new Date(habit.createdAt);
                          const daysSinceCreation = Math.floor((selectedDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                          if (daysSinceCreation >= habit.frequencyConfig.duration) {
                            return (
                              <>
                                <Trophy className="h-4 w-4 mr-2" />
                                <span className="hidden sm:inline">{t('habits.finished')}</span>
                                <span className="sm:hidden">Finished</span>
                              </>
                            );
                          }
                          return (
                            <>
                              <TrendingUp className="h-4 w-4 mr-2" />
                              <span className="hidden sm:inline">
                                {targetCount > 1 
                                  ? `${t('habits.logForDate')} (${completionCount}/${targetCount})`
                                  : (isTodaySelected ? t('habits.logForDate') : t('habits.logForDate', { date: format(selectedDate, 'PP') }))
                                }
                              </span>
                              <span className="sm:hidden">
                                {targetCount > 1 ? `${completionCount}/${targetCount}` : 'Log'}
                              </span>
                            </>
                          );
                        })()}
                      </motion.div>
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t('habits.progress')}</span>
                    <span>
                      {(() => {
                        const startDate = new Date(habit.createdAt);
                        startDate.setHours(0, 0, 0, 0);
                        const selectedDateStart = new Date(selectedDate);
                        selectedDateStart.setHours(0, 0, 0, 0);
                        const daysSinceCreation = Math.floor((selectedDateStart.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
                        if (daysSinceCreation >= habit.frequencyConfig.duration) {
                          return t('habits.finished');
                        }
                        return t('habits.dayCounter', { day: Math.max(1, daysSinceCreation + 1) });
                      })()}
                    </span>
                  </div>
                  {/* Segmented Progress Bar */}
                  <div className="flex h-2 w-full rounded-full overflow-hidden bg-muted/30">
                    {(() => {
                      const totalDots = calculateTotalDots(habit.frequencyConfig);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const startDate = new Date(habit.createdAt);
                      startDate.setHours(0, 0, 0, 0);
                      
                      return Array.from({ length: totalDots }).map((_, i) => {
                        const isCompleted = (habitProgressDots[habit.id]?.[i] || 0) === 1;
                        const segmentDate = new Date(startDate);
                        segmentDate.setDate(startDate.getDate() + i);
                        segmentDate.setHours(0, 0, 0, 0);
                        const isPast = segmentDate < today;

                        return (
                          <div
                            key={i}
                            className={cn(
                              "flex-1 h-full",
                              isCompleted
                                ? 'bg-[#7dda55]' // Namat green for completed
                                : isPast
                                  ? 'bg-[#7dda55]/25' // Light green for missed past days
                                  : 'bg-muted/30' // Light gray for future days
                            )}
                          />
                        );
                      });
                    })()}
                  </div>
                </div>
              </motion.div>
            );
          })}

          {/* Competition habits: same list as personal ones, marked with the
              competition's trophy chip (mobile TodayScreen, namat#36). */}
          {competitions.flatMap((competition) =>
            competition.habits.map((competitionHabit, habitIndex) => {
              const key = `${competition.id}_${competitionHabit.id}`;
              const isCompletedOnDate = competitionHabitStatus[key] || false;
              const isBusy = busyCompetitionHabit === key;

              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: habitIndex * 0.1 }}
                  className="space-y-2 border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="space-y-1">
                      {/* Mobile: Name wraps with trophy badge on the same row */}
                      <div className="sm:hidden">
                        <div className="grid grid-cols-[1fr,auto] gap-2 mb-2">
                          <span className="font-medium whitespace-normal break-words">{competitionHabit.name}</span>
                          <div className="justify-self-end">
                            <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800">
                              <Trophy className="h-3 w-3 mr-1" />
                              {competition.name}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Desktop: Original habit name and badges */}
                      <div className="hidden sm:flex items-center gap-2">
                        <span className="font-medium">{competitionHabit.name}</span>
                        <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800">
                          <Trophy className="h-3 w-3 mr-1" />
                          {competition.name}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Badge variant="outline" className="font-normal">
                            {categoryLabel(competitionHabit.category)}
                          </Badge>
                        </div>
                        {/* Mobile-only log button at end of this row */}
                        <div className="sm:hidden">
                          <Button
                            variant="ghost"
                            onClick={() => handleCompetitionHabitToggle(
                              competition,
                              competitionHabit.id,
                              competitionHabit.name,
                              competitionHabit.category,
                              isCompletedOnDate
                            )}
                            className={cn(
                              "h-8 px-3",
                              isCompletedOnDate
                                ? "bg-[#7dda55] hover:bg-red-500 text-white opacity-80"
                                : "bg-[#7dda55] hover:bg-[#6dc447] text-white"
                            )}
                            disabled={isFutureDate || isBusy}
                          >
                            <motion.div
                              className="flex items-center"
                              whileTap={{ scale: 0.95 }}
                              transition={{ type: "spring", stiffness: 400, damping: 17 }}
                            >
                              {isCompletedOnDate ? (
                                <>
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  <span className="text-xs">{t('habits.loggedOnDate')}</span>
                                </>
                              ) : isFutureDate ? (
                                <span className="text-xs">{t('habits.notifications.cannotLogFuture')}</span>
                              ) : (
                                <>
                                  <TrendingUp className="h-3 w-3 mr-1" />
                                  <span className="text-xs">{isTodaySelected ? t('habits.logForDate') : t('habits.logForDate')}</span>
                                </>
                              )}
                            </motion.div>
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Desktop: Original button layout */}
                    <div className="hidden sm:flex items-center justify-end gap-2 w-full sm:w-auto">
                      <Button 
                        variant="ghost" 
                        onClick={() => handleCompetitionHabitToggle(
                          competition,
                          competitionHabit.id,
                          competitionHabit.name,
                          competitionHabit.category,
                          isCompletedOnDate
                        )}
                        className={cn(
                          "h-9 px-4 flex-1 sm:flex-none",
                          isCompletedOnDate
                            ? "bg-[#7dda55] hover:bg-red-500 text-white opacity-80"
                            : "bg-[#7dda55] hover:bg-[#6dc447] text-white"
                        )}
                        disabled={isFutureDate || isBusy}
                      >
                        <motion.div
                          className="flex items-center"
                          whileTap={{ scale: 0.95 }}
                          transition={{ type: "spring", stiffness: 400, damping: 17 }}
                        >
                          {isCompletedOnDate ? (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              <span className="hidden sm:inline">{t('habits.loggedOnDate')}</span>
                            </>
                          ) : isFutureDate ? (
                            <>
                              <span className="hidden sm:inline">{t('habits.notifications.cannotLogFuture')}</span>
                            </>
                          ) : (
                            <>
                              <TrendingUp className="h-4 w-4 mr-2" />
                              <span className="hidden sm:inline">
                                {isTodaySelected ? t('habits.logForDate') : t('habits.logForDate')}
                              </span>
                            </>
                          )}
                        </motion.div>
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{t('habits.progress')}</span>
                      <span>
                        {(() => {
                          const now = new Date();
                          const startDate = new Date(competition.startDate);
                          const endDate = new Date(competition.endDate);
                          const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                          const daysSinceStart = Math.max(0, Math.ceil((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)));
                          const currentDay = Math.min(daysSinceStart + 1, totalDays);
                          return `${currentDay}/${totalDays} ${t('habits.days')}`;
                        })()}
                      </span>
                    </div>
                    {/* Segmented Progress Bar */}
                    <div className="flex h-2 w-full rounded-full overflow-hidden bg-muted/30">
                      {(() => {
                        const startDate = new Date(competition.startDate);
                        const endDate = new Date(competition.endDate);
                        const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        
                        return Array.from({ length: totalDays }, (_, i) => {
                          const segmentDate = new Date(startDate);
                          segmentDate.setDate(startDate.getDate() + i);
                          segmentDate.setHours(0, 0, 0, 0);
                          const isPast = segmentDate < today;
                          const isCompleted = competitionProgressDots[key]?.[i] || false;

                          return (
                            <div
                              key={i}
                              className={cn(
                                "flex-1 h-full",
                                isCompleted
                                  ? 'bg-[#7dda55]' // Namat green for completed
                                  : isPast
                                    ? 'bg-[#7dda55]/25' // Light green for missed past days
                                    : 'bg-muted/30' // Light gray for future days
                              )}
                            />
                          );
                        });
                      })()}
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}

        </div>
      </CardContent>

      <AddHabitDialog
        open={showAddHabit}
        onOpenChange={setShowAddHabit}
        onHabitCreated={refreshHabits}
      />

      {habitToEdit && (
        <EditHabitDialog
          habit={habitToEdit}
          open={!!habitToEdit}
          onOpenChange={(open) => !open && setHabitToEdit(null)}
          onHabitUpdated={refreshHabits}
        />
      )}

      <AlertDialog open={!!habitToDelete} onOpenChange={(open) => !open && setHabitToDelete(null)}>
        <AlertDialogContent className={`${language === 'ar' ? 'rtl' : 'ltr'}`}>
          <AlertDialogHeader className={`${language === 'ar' ? 'text-right pr-0' : 'text-left pl-0'}`}>
            <AlertDialogTitle className={language === 'ar' ? 'text-right' : 'text-left'}>{t('habits.confirmations.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription className={language === 'ar' ? 'text-right' : 'text-left'}>
              {t('habits.confirmations.deleteMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={`${language === 'ar' ? 'flex-row-reverse' : ''} gap-2`}>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteHabit}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('habits.actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}